import { useEffect, useMemo, useRef } from 'react'
import cn from 'classnames'
import { LEASE_CHAIN_CONVEYANCE_TYPES, TITLE_CHAIN_CONVEYANCE_TYPES } from '@/constants'
import { formatDate } from '@/utils/date'
import ConveyanceDocRow from './ConveyanceDocRow'
import { getConveyanceByDocId, sumConveyance, conveyanceRound } from './ConveyanceUtils'

const ConveyanceDoc = ({ document, parties, rsDocJoin, setEditRsDocJoin, showHideAdd, conveyanceType }) => {
    
    const tBodyRef = useRef()
    const lastInteractionRef = useRef(null)
    
    const chainHeaders = useMemo(() =>
        conveyanceType === 'title' ? TITLE_CHAIN_CONVEYANCE_TYPES : LEASE_CHAIN_CONVEYANCE_TYPES,
    [conveyanceType])
    
    const getTotalConveyancesByDoc = field =>
        conveyanceRound(parties.reduce((acc, curr) => {
            
            const conveyance = getConveyanceByDocId(curr, document.documentId, field) || {}
            
            return sumConveyance(field)(acc, conveyance)
            
        }, 0))
    
    useEffect(() => {
        if (tBodyRef.current) {
            const groupElems = [...tBodyRef.current.querySelectorAll('tr[data-group-id]')]
            
            groupElems.forEach((c, _, list) => {
                const groupId = c.dataset.groupId
                
                const onEnter = () => {
                    list.forEach(l => {
                        if (l.dataset.groupId === groupId)
                            l.classList.add('group-hovering')
                    })
                }
                
                const onLeave = () => {
                    list.forEach(l => l.classList.remove('group-hovering'))
                }
                
                c.addEventListener('mouseenter', onEnter)
                c.addEventListener('mouseleave', onLeave)
                
                return () => {
                    c.removeEventListener('mouseenter', onEnter)
                    c.removeEventListener('mouseleave', onLeave)
                }
            })
        }
    }, [])
    
    const handleMouseDown = () => {
        lastInteractionRef.current = 'click'
    }
    
    const handleKeyDown = event => {
        if (event.key === 'Tab') {
            lastInteractionRef.current = 'tab'
        }
    }
    
    return (
        <div
            key={document.documentId}
            id={document.documentId}
            className={cn('flex flex-col', {
                'title-document': conveyanceType === 'title',
                'leasehold-document': conveyanceType === 'leasehold',
            })}>
            <table 
                onMouseDown={handleMouseDown}
                onKeyDown={handleKeyDown}
                className={cn('bg-base-100 text-sm border-l border-parse-800', {
                    'title-table': conveyanceType === 'title',
                    'leasehold-table': conveyanceType === 'leasehold',
                })}>
                <thead>
                    <tr className="sticky top-0" >
                        <td className="!p-0" colSpan={chainHeaders.length}>
                            <div className="flex flex-col items-center gap-2 pb-2 border-b
                                border-parse-800 bg-base-200 text-sm sticky top-0 pt-2">
                                <span
                                    className="px-3 line-clamp-1 cursor-pointer underline text-primary"
                                    onClick={() => {
                                        const rsEditDocJoin = rsDocJoin.find(it => 
                                            document.documentId === it.documentId)
                                        
                                        setEditRsDocJoin(rsEditDocJoin)
                                        showHideAdd(true)
                                    }}>
                                    {document.name}&nbsp;
                                </span>
                                <span className="px-3 line-clamp-1">{document.value.instrumentType}&nbsp;</span>
                                <span className="px-3">
                                    {[
                                        document.value.bookType,
                                        document.value.bookVolume,
                                        document.value.bookPage,
                                    ].filter(v => !!v).join(' / ')}&nbsp;
                                </span>
                                <span className="px-3">{document.value.instrumentNumber}&nbsp;</span>
                                <span className="px-3">{formatDate(document.value.fileDate)}&nbsp;</span>
                                <span className="px-3">{formatDate(document.value.instrumentDate)}&nbsp;</span>
                                <span className="px-3 line-clamp-1" 
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={document.value.grantors?.map(g => g.name).join('; ') || ''}>
                                    {document.value.grantors?.map(g => g.name).join('; ') || ''}&nbsp;
                                </span>
                                <span className="px-3 line-clamp-1"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={document.value.grantees?.map(g => g.name).join('; ') || ''}>
                                    {document.value.grantees?.map(g => g.name).join('; ') || ''}&nbsp;
                                </span>
                                <span className="px-3 line-clamp-1"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={document.value.comments}>
                                    {document.value.comments}&nbsp;
                                </span>
                                <div className="flex px-2 gap-4 w-full">
                                    {chainHeaders.map(type => {
                                        const total = getTotalConveyancesByDoc(type)
                                        const textColor = parseFloat(total) !== 0.0 ? 'text-error' : 'text-slate-500'
                                        
                                        return (
                                            <span
                                                key={`sumCheck-${type}`}
                                                className={`flex-1 text-center ${textColor}`}>
                                                {total}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr className="bg-base-100 sticky top-[288px]">
                        {chainHeaders.map(type => <th key={`header-${type}`}>{type}</th>)}
                    </tr>
                </thead>
                <tbody ref={tBodyRef}>
                    {parties.map((p, idx) => <ConveyanceDocRow
                        key={`row-${document.documentId}-${p.id}`}
                        x={idx}
                        documentId={document.documentId}
                        party={p}
                        conveyanceType={conveyanceType}
                        chainHeaders={chainHeaders}
                        getLastInteractionRef={() => lastInteractionRef} />)}
                </tbody>
            </table>
        </div>
    )
}

export default ConveyanceDoc
